import { checkDate } from "common/helpers/Date";
import { AccessRecord, IAccessData } from "common/records/Access";
import { IOrgData, orgFromJSON, OrgRecord } from "common/records/Org";
import { IUserPreferenceData, userPreferenceFromJSON, UserPreferenceRecord } from "common/records/UserPreference";
import { Nullable } from "common/types";
import { List, Map, Record } from "immutable";
import { DateStringNumber } from ".";

export const userFromJSON = (data: Partial<IUserData>): UserRecord => {
  const record: IUserRecord = { ...(data as any) };

  if (data.accesses) {
    record.accesses = List(
      new Set(
        data.accesses.map((a) => {
          if (typeof a === "number") return a;
          return a.id;
        }),
      ),
    );
  }

  if (data.permissions) {
    record.permissions = Map<AclKeys, AclValues[]>(data.permissions as any);
  }
  if (data?.preferences) {
    record.preferences = userPreferenceFromJSON(data.preferences);
  }

  if (data.org) {
    record.org = orgFromJSON(data.org);
  }

  if (data.preferences) {
    record.preferences = userPreferenceFromJSON(data.preferences);
  }

  if (data.created_at) {
    record.created_at = checkDate(data.created_at);
  }

  if (data.errors) {
    record.errors = List(data.errors);
  }

  return new UserRecord(record);
};

export const generatePassword = (): string => {
  const length = 12;
  const numbers = "0123456789";
  const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const specialChars = "!@#$%^&*()_+~`|}{[]:;?><,./-=";
  const charsetLength = charset.length;
  const specialCharsLength = specialChars.length;
  let password = "";

  // Ensure at least one uppercase, one lowercase, one special character, and one number
  password += charset.charAt(Math.floor(Math.random() * 26) + 26); // Uppercase
  password += charset.charAt(Math.floor(Math.random() * 26)); // Lowercase
  password += specialChars.charAt(Math.floor(Math.random() * specialCharsLength)); // Special character
  password += numbers.charAt(Math.floor(Math.random() * 10)); // Number

  for (let i = 4; i < length; ++i) {
    password += charset.charAt(Math.floor(Math.random() * charsetLength));
  }

  // Shuffle the password to ensure randomness
  password = password
    .split("")
    .sort(() => 0.5 - Math.random())
    .join("");

  return password;
};

export const getFullName = (user: Nullable<UserRecord>, active = true): string => {
  if (user) {
    return `${user.first_name || ""} ${user.last_name || ""}`.trim() + (active ? "" : " (INACTIVE)");
  }

  return "Unset";
};

export const fullNameAndEmail = (user: Nullable<UserRecord>): string => {
  return `${getFullName(user)} - ${user?.email || "No Email"}`.trim();
};

export const placeholderEmail = (orgName: string): string => {
  const prefix = "placeholder+";
  const cleanOrgName = orgName.replace(/\W/g, "").toLowerCase();
  const random = Math.floor(Math.random() * 100000);
  const postfix = "@oneclickcontractor.com";

  return `${prefix}${cleanOrgName}${random}${postfix}`;
};

export const isPlaceholderEmail = (email: Nullable<string>): boolean => {
  if (!email) return false;
  // (email =~ /placeholder+.*@(remotesf|oneclickcontractor)\.com/) == 0
  return /placeholder\+.*@(remotesf|oneclickcontractor)\.com/.test(email);
};

export type AclKeys =
  | "org"
  | "price_list"
  | "product"
  | "product_option_group"
  | "product_option"
  | "payment_term"
  | "token"
  | "estimate_template"
  | "pattern"
  | "report"
  | "user"
  | "crm_user"
  | "presentation"
  | "event"
  | "events_result"
  | "events_result_reason"
  | "job"
  | "estimate"
  | "screen_share"
  | "visualization"
  | "storm"
  | "area"
  | "lead"
  | "weather_property"
  | "billing_account"
  | "billing_customer"
  | "billing_invoice"
  | "billing_order"
  | "billing_subscription"
  | "billing_invoice_item"
  | "billing_fee_subscription"
  | "billing_plan"
  | "billing_fee"
  | "global"
  | "admin_panel"
  | "header_panel"
  | "eagleview"
  | "quick_measure"
  | "plnar"
  | "hover"
  | "roof_scope"
  | "discount"
  | "tool"
  | "product_process"
  | "price_list_process"
  | "finance_option"
  | "configuration"
  | "flags"
  | "validation"
  | "payment"
  | "payment_method"
  | "estimate_templates"
  | "job_metadata"
  | "custom_form";

export type AclValues = StandardAclValues | AclFlags;

export type StandardAclValues =
  | "read"
  | "create"
  | "update"
  | "delete"
  | "read_assigned"
  | "create_assigned"
  | "update_assigned"
  | "delete_assigned"
  | "read_self"
  | "create_self"
  | "update_self"
  | "delete_self"
  | "acl_template"
  | "acl_custom"
  | "marketsharp"
  | "status"
  | "events"
  | "email"
  | "price_list"
  | "tools"
  | "billing"
  | "org"
  | "apl_csv"
  | "job"
  | "user"
  | "link"
  | "info"
  | "preferences"
  | "settings"
  | "jobs"
  | "child_orgs"
  | "users"
  | "billing"
  | "documents"
  | "presentations"
  | "price_list_editor"
  | "measurement_links"
  | "payment_terms"
  | "pattern_editor"
  | "integrations"
  | "email_history"
  | "statistics"
  | "leads"
  | "weather_properties"
  | "reports"
  | "calendar"
  | "signed_docs"
  | "update_pricing"
  | "update_markup"
  | "update_option_quantity"
  | "custom"
  | "order"
  | "update_billing"
  | "duplicate_org"
  | "payments"
  | "webhooks"
  | "job_types"
  | "billing_info"
  | "global_settings"
  | "estimator"
  | "measurements"
  | "products_pricing_job"
  | "options_pricing_job"
  | "duplicate_price_list_job"
  | "images_to_leaves_job"
  | "push_columns_job"
  | "pricing_job"
  | "multi_duplicate_product_tree_job"
  | "option_group_job"
  | "duplicate"
  | "global_settings"
  | "analytics"
  | "estimator"
  | "measurements"
  | "financing"
  | "sharing"
  | "job_status"
  | "products"
  | "access_management"
  | "sample_jobs"
  | "create_global"
  | "update_global"
  | "delete_global"
  | "import"
  | "email_text"
  | "onscreen"
  | "payment_report"
  | "deposit_report"
  | "chargeback_report"
  | "product_csv"
  | "commission"
  | "commission_pdf"
  | "cost_sheet"
  | "remeasure_csv"
  | "surcharge_configuration"
  | "csv_export";

export type AclFlags =
  | "jobs_uiux"
  | "pl_split_main"
  | "pl_split_split"
  | "one_click_crm"
  | "navigator"
  | "job_resulting";

export type UserPermissions = { [k in AclKeys]?: AclValues[] };
export interface IUserData {
  id: number;
  uuid: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  accepted_tos: boolean;
  created_at: DateStringNumber;
  org_id: number;
  preferences_id: number;
  permissions: UserPermissions;
  url: string;
  accesses: IAccessData[];
  dirty_accesses: IAccessData[];
  accesses_count: number;
  current_access_uid?: string;
  org?: IOrgData;
  preferences?: IUserPreferenceData;
  loading: boolean;
  errors: string[];
  time_zone: string;
}

export interface IUserRecord {
  id: number;
  uuid: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  accepted_tos: boolean;
  created_at: Date;
  org_id: number;
  preferences_id: number;
  url: string;
  accesses: List<number>;
  dirty_accesses: List<AccessRecord>;
  accesses_count: number;
  permissions: Map<AclKeys, AclValues[]>;
  loading: boolean;
  errors: List<string>;
  org?: Nullable<OrgRecord>;
  preferences?: Nullable<UserPreferenceRecord>;
  password: string;
  password_confirmation: string;
  current_access_uid: string;
  time_zone: string;
}

const defaultUserProps: IUserRecord = {
  id: 0,
  uuid: "",
  email: "",
  first_name: "",
  last_name: "",
  phone: "",
  accepted_tos: false,
  created_at: new Date(),
  org_id: 0,
  preferences_id: 0,
  url: "",
  accesses: List([]),
  dirty_accesses: List([]),
  accesses_count: 0,
  permissions: Map<AclKeys, AclValues[]>(),
  loading: false,
  errors: List<string>(),
  org: null,
  current_access_uid: "",
  time_zone: "",
  password: "",
  preferences: null,
  password_confirmation: "",
};

export type Permissions = Map<AclKeys, AclValues[]>;

export class UserRecord extends Record(defaultUserProps) implements IUserRecord {}

export const emptyUserValue = userFromJSON({} as IUserData);
